import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Sparkles,
  Users,
  HeartHandshake,
  Plane,
  BedDouble,
  Clock,
  ShieldCheck,
  Target,
  MessageSquare,
  Check,
  BadgeCheck,
} from "lucide-react";
import Reveal from "../components/Reveal";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "../hooks/use-toast";
import { jobsPage } from "../mock/jobs";
import { venues } from "../mock/venues";

const workplaceShots = venues.flatMap((v) =>
  (v.gallery || []).slice(0, 2).map((g) => ({ ...g, venue: v.name }))
).slice(0, 6);

const ICONS = {
  Sparkles,
  Users,
  HeartHandshake,
  Plane,
  BedDouble,
  Clock,
  ShieldCheck,
  Target,
};

const SectionTitle = ({ kicker, title }) => (
  <Reveal>
    {kicker ? (
      <p className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-primary">
        {kicker}
      </p>
    ) : null}
    <h2 className="font-serif text-3xl leading-tight text-foreground sm:text-4xl">{title}</h2>
    <div className="section-divider ml-0 mt-6" />
  </Reveal>
);

const PerkCard = ({ card, delay }) => {
  const Icon = ICONS[card.icon] || Sparkles;
  return (
    <Reveal delay={delay}>
      <article className="h-full rounded-xl border border-border bg-card p-7 transition-colors hover:border-primary/40">
        <span className="mb-5 flex h-11 w-11 items-center justify-center rounded-full bg-primary/10">
          <Icon className="h-5 w-5 text-primary" />
        </span>
        <h3 className="font-serif text-xl leading-snug text-foreground">{card.title}</h3>
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{card.text}</p>
      </article>
    </Reveal>
  );
};

const ApplicationForm = () => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    experience: "",
    availability: "",
    notes: "",
  });

  const submit = (e) => {
    e.preventDefault();
    const list = JSON.parse(localStorage.getItem("sbg_applications") || "[]");
    localStorage.setItem(
      "sbg_applications",
      JSON.stringify([{ ...form, createdAt: new Date().toISOString() }, ...list])
    );
    toast({
      title: "Application submitted",
      description:
        "All enquiries are strictly confidential. Management will be in touch privately.",
    });
    setForm({ name: "", phone: "", experience: "", availability: "", notes: "" });
  };

  return (
    <form
      onSubmit={submit}
      data-testid="jobs-application-form"
      className="space-y-5 rounded-2xl border border-border bg-card p-8"
    >
      <h3 className="font-serif text-2xl text-foreground">Confidential Enquiry</h3>
      <p className="text-sm text-muted-foreground">
        Applicants must be 18 or over and provide valid photo identification. Ladies of all
        nationalities are welcome. We reply by SMS only, so please no direct telephone calls.
      </p>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="w-name">Name *</Label>
          <Input
            id="w-name"
            required
            data-testid="jobs-name-input"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="First name is fine"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="w-phone">Phone *</Label>
          <Input
            id="w-phone"
            required
            type="tel"
            data-testid="jobs-phone-input"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            placeholder="0400 000 000"
          />
        </div>
        <div className="space-y-2">
          <Label>Experience</Label>
          <Select
            value={form.experience}
            onValueChange={(v) => setForm({ ...form, experience: v })}
          >
            <SelectTrigger data-testid="jobs-experience-trigger">
              <SelectValue placeholder="Select experience" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="No experience">No experience</SelectItem>
              <SelectItem value="Less than 1 year">Less than 1 year</SelectItem>
              <SelectItem value="1-3 years">1-3 years</SelectItem>
              <SelectItem value="3+ years">3+ years</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Availability</Label>
        <Select
          value={form.availability}
          onValueChange={(v) => setForm({ ...form, availability: v })}
        >
          <SelectTrigger data-testid="jobs-availability-trigger">
            <SelectValue placeholder="Select availability" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Full time">Full time</SelectItem>
            <SelectItem value="Part time">Part time</SelectItem>
            <SelectItem value="Weekends only">Weekends only</SelectItem>
            <SelectItem value="Nights only">Nights only</SelectItem>
            <SelectItem value="Travelling / short stay">Travelling / short stay</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="w-notes">Anything else?</Label>
        <Textarea
          id="w-notes"
          rows={4}
          data-testid="jobs-notes-input"
          className="resize-none"
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          placeholder="Relocation assistance, immediate start, preferred hours..."
        />
      </div>

      <button type="submit" data-testid="jobs-submit-btn" className="btn-pink-glow w-full">
        Send Confidential Enquiry
      </button>
    </form>
  );
};

const Employment = () => {
  const { hero, sections, enquiry, important } = jobsPage;

  return (
    <div data-testid="jobs-page">
      {/* hero */}
      <section className="border-b border-border bg-[#fdf1f7]">
        <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
          <Reveal>
            <Link
              to="/"
              data-testid="jobs-back-link"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-muted-foreground transition-colors hover:text-primary"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back to the guide
            </Link>
          </Reveal>
          <Reveal delay={80}>
            <p className="mt-9 text-xs font-semibold uppercase tracking-[0.2em] text-[#a32069]">
              {hero.eyebrow}
            </p>
            <h1 className="mt-5 font-serif text-4xl leading-[1.1] tracking-tight text-foreground sm:text-5xl lg:text-6xl">
              {hero.title}
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <p className="mt-8 max-w-3xl text-base leading-relaxed text-muted-foreground md:text-lg">
              {hero.intro}
            </p>
          </Reveal>

          <Reveal delay={240}>
            <div className="mt-11 rounded-2xl border border-[#f0a9c9] bg-white p-8 sm:p-10">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#a32069]">
                {hero.bandKicker}
              </p>
              <p className="mt-3 font-serif text-3xl leading-tight text-foreground sm:text-4xl">
                {hero.bandHeadline}
              </p>
              <p className="mt-6 leading-relaxed text-muted-foreground">{hero.bandText}</p>
              <a href="#apply" data-testid="jobs-hero-apply-btn" className="btn-pink-glow mt-8">
                Apply Now
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* content sections */}
      {sections.map((s, i) => (
        <section
          key={s.id}
          id={s.id}
          className={`scroll-mt-28 py-16 sm:py-20 ${i % 2 === 1 ? "bg-card" : ""}`}
        >
          <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            <SectionTitle kicker={s.kicker} title={s.title} />
            <div className="mt-8 space-y-5">
              {s.paragraphs.map((p, j) => (
                <Reveal key={j} delay={j * 60}>
                  <p className="leading-relaxed text-muted-foreground">{p}</p>
                </Reveal>
              ))}
            </div>

            {s.cards.length ? (
              <div
                className={`mt-11 grid gap-6 ${
                  s.cards.length === 2 ? "sm:grid-cols-2" : "lg:grid-cols-3"
                }`}
              >
                {s.cards.map((c, j) => (
                  <PerkCard key={c.title} card={c} delay={j * 90} />
                ))}
              </div>
            ) : null}

            {s.list ? (
              <Reveal delay={120}>
                <div className="mt-12 rounded-2xl border border-border bg-background p-8">
                  <h3 className="font-serif text-xl text-foreground">{s.listTitle}</h3>
                  <ul className="mt-6 space-y-4">
                    {s.list.map((item) => (
                      <li key={item} className="flex items-start gap-3">
                        <Check className="mt-1 h-4 w-4 flex-shrink-0 text-primary" />
                        <span className="leading-relaxed text-muted-foreground">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ) : null}

            {s.outro ? (
              <Reveal delay={160}>
                <p className="mt-9 border-l-2 border-primary/60 bg-muted/40 px-6 py-5 leading-relaxed text-muted-foreground">
                  {s.outro}
                </p>
              </Reveal>
            ) : null}
          </div>
        </section>
      ))}

      {/* where you'd work */}
      <section id="where-youd-work" className="scroll-mt-28 bg-[#fdf1f7] py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <SectionTitle
            kicker="A look inside"
            title="Where You'd Be Working"
          />
          <p className="mt-8 max-w-3xl leading-relaxed text-muted-foreground">
            Every room is professionally cleaned, fully equipped and privately serviced. These are
            real rooms from the establishments we work with across Caulfield South and South
            Melbourne.
          </p>
          <div className="mt-11 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {workplaceShots.map((shot, i) => (
              <Reveal key={shot.img} delay={i * 70}>
                <figure
                  data-testid="jobs-workplace-shot"
                  className="h-full overflow-hidden rounded-xl border border-[#f0a9c9] bg-white"
                >
                  <div className="aspect-[4/3] w-full overflow-hidden">
                    <img
                      src={shot.img}
                      alt={shot.caption}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                    />
                  </div>
                  <figcaption className="px-5 py-4">
                    <span className="block text-[0.68rem] font-semibold uppercase tracking-[0.16em] text-[#a32069]">
                      {shot.venue}
                    </span>
                    <span className="mt-1.5 block text-sm leading-relaxed text-muted-foreground">
                      {shot.caption}
                    </span>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* confidential enquiry + form */}
      <section id="apply" className="scroll-mt-28 bg-[#fdf1f7] py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div id={enquiry.id} className="scroll-mt-28">
            <SectionTitle kicker={enquiry.kicker} title={enquiry.title} />
          </div>
          <div className="mt-10 grid gap-10 lg:grid-cols-[1fr_1fr]">
            <div className="space-y-6">
              {enquiry.paragraphs.map((p, i) => (
                <Reveal key={i}>
                  <p className="leading-relaxed text-muted-foreground">{p}</p>
                </Reveal>
              ))}
              <Reveal delay={80}>
                <a
                  href={`sms:${enquiry.smsNumber.replace(/\s/g, "")}`}
                  data-testid="jobs-sms-link"
                  className="flex items-center gap-4 rounded-xl border border-[#f0a9c9] bg-white p-6 transition-colors hover:border-primary"
                >
                  <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-primary/10">
                    <MessageSquare className="h-5 w-5 text-primary" />
                  </span>
                  <span>
                    <span className="block text-xs uppercase tracking-[0.18em] text-muted-foreground">
                      {enquiry.smsLabel}
                    </span>
                    <span className="mt-1 block font-serif text-lg text-foreground">
                      {enquiry.smsNumber}
                    </span>
                    <span className="mt-1 block text-xs text-muted-foreground">
                      {enquiry.smsNote}
                    </span>
                  </span>
                </a>
              </Reveal>
              <Reveal delay={120}>
                <p className="leading-relaxed text-muted-foreground">{enquiry.outro}</p>
              </Reveal>
            </div>
            <Reveal delay={140}>
              <ApplicationForm />
            </Reveal>
          </div>
        </div>
      </section>

      {/* important application information */}
      <section id={important.id} className="scroll-mt-28 py-16 sm:py-20">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <SectionTitle title={important.title} />
          <div className="mt-10 grid gap-6 sm:grid-cols-2">
            {important.items.map((it, i) => (
              <Reveal key={it.title} delay={i * 90}>
                <article className="h-full rounded-xl border border-border bg-card p-7">
                  <span className="mb-5 flex h-11 w-11 items-center justify-center rounded-full bg-primary/10">
                    <BadgeCheck className="h-5 w-5 text-primary" />
                  </span>
                  <h3 className="font-serif text-xl text-foreground">{it.title}</h3>
                  <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{it.text}</p>
                </article>
              </Reveal>
            ))}
          </div>
          <Reveal delay={140}>
            <p className="mt-10 text-center font-serif text-xl leading-relaxed text-foreground sm:text-2xl">
              {important.outro}
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Employment;
